import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import CartDrawer from './components/shop/CartDrawer';

// Pages - Shop
import HomePage from './pages/shop/HomePage';
import ShopPage from './pages/shop/ShopPage';
import ProductPage from './pages/shop/ProductPage';
import CartPage from './pages/shop/CartPage';
import CheckoutPage from './pages/shop/CheckoutPage';
import OrderConfirmPage from './pages/shop/OrderConfirmPage';
import OrdersPage from './pages/shop/OrdersPage';

// Pages - Auth
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ProfilePage from './pages/auth/ProfilePage';

// Pages - Blog
import BlogPage from './pages/blog/BlogPage';
import BlogPostPage from './pages/blog/BlogPostPage';

// Pages - Admin
import AdminLayout from './components/admin/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminBlogPage from './pages/admin/AdminBlogPage';
import AdminInventoryPage from './pages/admin/AdminInventoryPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30000, retry: 1 } } });

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user?.role === 'admin' ? children : <Navigate to="/" />;
}

function ShopLayout({ children }) {
  return (
    <>
      <Navbar />
      <CartDrawer />
      <main>{children}</main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontFamily: 'Inter, sans-serif', borderRadius: '10px', fontSize: '0.9rem' } }} />
            <Routes>
              {/* Shop */}
              <Route path="/" element={<ShopLayout><HomePage /></ShopLayout>} />
              <Route path="/shop" element={<ShopLayout><ShopPage /></ShopLayout>} />
              <Route path="/product/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />
              <Route path="/cart" element={<ShopLayout><CartPage /></ShopLayout>} />
              <Route path="/checkout" element={<PrivateRoute><ShopLayout><CheckoutPage /></ShopLayout></PrivateRoute>} />
              <Route path="/order-confirm/:id" element={<ShopLayout><OrderConfirmPage /></ShopLayout>} />
              <Route path="/orders" element={<PrivateRoute><ShopLayout><OrdersPage /></ShopLayout></PrivateRoute>} />

              {/* Auth */}
              <Route path="/login" element={<ShopLayout><LoginPage /></ShopLayout>} />
              <Route path="/register" element={<ShopLayout><RegisterPage /></ShopLayout>} />
              <Route path="/profile" element={<PrivateRoute><ShopLayout><ProfilePage /></ShopLayout></PrivateRoute>} />

              {/* Blog */}
              <Route path="/blog" element={<ShopLayout><BlogPage /></ShopLayout>} />
              <Route path="/blog/:slug" element={<ShopLayout><BlogPostPage /></ShopLayout>} />

              {/* Admin */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<DashboardPage />} />
                <Route path="products" element={<AdminProductsPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="blog" element={<AdminBlogPage />} />
                <Route path="inventory" element={<AdminInventoryPage />} />
                <Route path="users" element={<AdminUsersPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
